'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation'; // <-- Добавляем хуки Next.js
import { dictionaries, Locale } from '@/i18n/dictionaries';

export type { Locale };

type LanguageContextType = {
  locale: Locale;
  setLocale: (lang: Locale) => void;
  t: typeof dictionaries['ru'];
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({
  children,
  initialLocale,
}: {
  children: React.ReactNode;
  initialLocale: Locale;
}) {
  const [locale, setLocaleState] = useState(initialLocale);
  
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Синхронизация при загрузке (если необходимо)
  useEffect(() => {
    setLocaleState(initialLocale);
  }, [initialLocale]);

  const setLocale = (newLang: Locale) => {
    if (newLang === locale) return;

    setLocaleState(newLang); // Оптимистично меняем стейт

    // Сохраняем куки
    const ONE_YEAR = 31536000;
    document.cookie = `app_locale=${newLang}; path=/; max-age=${ONE_YEAR}; samesite=lax`;

    // Формируем новый URL с помощью Next.js роутера
    const segments = window.location.pathname.split('/');
    if (segments.length > 1 && (segments[1] === 'ru' || segments[1] === 'en')) {
      segments[1] = newLang;
    } else {
      segments.splice(1, 0, newLang);
    }

const currentSearchParams = searchParams.toString();
// Убираем слеш на конце, если это не просто корень, чтобы избежать цепочки редиректов Next.js
let cleanPath = segments.join('/').replace(/\/$/, '');
if (!cleanPath) cleanPath = '/';

const newPath = cleanPath + (currentSearchParams ? `?${currentSearchParams}` : '');

    // Мгновенный переход без перезагрузки страницы!
    router.push(newPath);
    router.refresh(); // Принудительно заставляем сервер перечитать куки/заголовки
  };

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t: dictionaries[locale] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}